import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/apiClient";
import { runAsyncJob } from "../lib/llmJob";
import { Loader2, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { dueAttentionCount } from "./DueSoonBanner";

const SEVERITY_WEIGHT = { high: 3, medium: 2, low: 1 };

export function computeComplianceScore(compliance) {
  const items = compliance?.items || [];
  const counted = items.filter((i) => i.status !== "not_applicable");
  if (counted.length === 0) return null;
  const weightedTotal = counted.reduce((a, i) => a + (SEVERITY_WEIGHT[i.severity] || 1), 0);
  const weightedDone = counted
    .filter((i) => i.status === "completed")
    .reduce((a, i) => a + (SEVERITY_WEIGHT[i.severity] || 1), 0);
  const baseScore = weightedTotal > 0 ? Math.round((weightedDone / weightedTotal) * 100) : 100;
  const highOpen = counted.filter((i) => i.severity === "high" && i.status !== "completed").length;
  const totalOpen = counted.filter((i) => i.status !== "completed").length;
  // Clamp lower-bound for any open high-sev items (never claim "covered" if a high-sev is open)
  const score = highOpen > 0 ? Math.min(baseScore, 79) : baseScore;
  return {
    score,
    total: counted.length,
    done: counted.filter((i) => i.status === "completed").length,
    highOpen,
    totalOpen,
  };
}

const scoreColor = (s) => {
  if (s >= 90) return "#059669";
  if (s >= 70) return "#4A4458";
  if (s >= 50) return "#F59E0B";
  return "#DC2626";
};

const scoreLabel = (s) => {
  if (s >= 90) return "Covered";
  if (s >= 70) return "On track";
  if (s >= 50) return "At risk";
  return "Exposed";
};

export default function ComplianceScoreCard({ business, onUpdate }) {
  const [running, setRunning] = useState(false);
  const canRun = !!business?.plan && business?.my_role !== "viewer";
  if (!business?.plan) return null;

  const score = computeComplianceScore(business.compliance);
  // Jun 2026 · Badge Everywhere — same red due count as the command bars.
  const dueCount = dueAttentionCount(business);

  const run = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setRunning(true);
    try {
      const { result: data } = await runAsyncJob(api, { method: "post", url: `/businesses/${business.id}/compliance/generate` });
      onUpdate?.({ generated_at: data.generated_at, items: data.items });
      toast.success(`Mapped ${data.items.length} requirements`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Compliance scan failed");
    } finally {
      setRunning(false);
    }
  };

  if (!score) {
    return (
      <button
        onClick={run}
        disabled={!canRun || running}
        data-testid={`run-compliance-scan-${business.id}`}
        className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] px-2.5 py-1.5 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors disabled:opacity-50"
      >
        {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
        {running ? "Scanning" : "Scan compliance"}
      </button>
    );
  }

  const color = scoreColor(score.score);
  const showWarn = score.highOpen > 0;

  return (
    <Link
      to={`/compliance?business=${business.id}`}
      title={`${score.done}/${score.total} done${showWarn ? ` · ${score.highOpen} high-severity open` : ""}`}
      data-testid={`compliance-badge-${business.id}`}
      onClick={(e) => e.stopPropagation()}
      className="relative mt-3 group inline-flex items-stretch border border-neutral-300 hover:border-neutral-900 transition-colors"
    >
      {dueCount > 0 && (
        <span
          className="absolute -top-2 -right-2 min-w-[16px] h-4 px-1 flex items-center justify-center bg-[#DC2626] text-white text-[10px] font-black leading-none tabular-nums"
          data-testid="compliance-tile-due-count"
          aria-label={`${dueCount} item${dueCount === 1 ? "" : "s"} due or overdue`}
        >
          {dueCount > 9 ? "9+" : dueCount}
        </span>
      )}
      <div
        className="flex items-center justify-center px-3 font-display font-black text-base tracking-tighter text-white"
        style={{ background: color }}
      >
        {score.score}
      </div>
      <div className="flex flex-col items-start px-3 py-1.5">
        <span className="text-[9px] uppercase tracking-[0.2em] text-neutral-500">Compliance</span>
        <span className="text-[11px] font-bold flex items-center gap-1" style={{ color }}>
          {scoreLabel(score.score)}
          {showWarn && <AlertTriangle className="w-3 h-3" />}
        </span>
      </div>
      <div className="flex items-center px-2 text-[9px] uppercase tracking-[0.2em] text-neutral-500 group-hover:text-black border-l border-neutral-200">
        {score.done}/{score.total}
      </div>
    </Link>
  );
}
